"use client";

import { useCallback, useEffect, useState } from "react";
import { usePlatform } from "@/platform/PlatformProvider";
import { resolveLlmUrl } from "@/platform/resolve-url";
import { getVybeToken, useVybeAuthStore } from "@/lib/vybe-auth";

/** Daily AI budget snapshot returned by GET /api/agent/credits. `limit: -1` = unlimited (Desk). */
export interface AiCredits {
  plan: string;
  limit: number;
  used: number;
  remaining: number;
}

/** True when the plan has no daily cap (Desk / unlimited). */
export function isUnlimited(c: AiCredits | null): boolean {
  return !!c && c.limit < 0;
}

/** Milliseconds until the next UTC midnight — when the daily budget resets. */
export function msUntilUtcReset(now = Date.now()): number {
  const d = new Date(now);
  const next = Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate() + 1,
    0,
    0,
    0,
  );
  return Math.max(0, next - now);
}

/** Human "6h 12m" countdown to the next UTC reset. */
export function formatUtcResetCountdown(now = Date.now()): string {
  let s = Math.floor(msUntilUtcReset(now) / 1000);
  const h = Math.floor(s / 3600);
  s -= h * 3600;
  const m = Math.floor(s / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

/**
 * Reads the signed-in user's daily AI budget from the LLM server. The counter is authoritative server-side;
 * this hook only mirrors it for the sidebar meter. Call `refresh()` after each agent request settles.
 */
export function useAiCredits(): {
  credits: AiCredits | null;
  refresh: () => Promise<void>;
} {
  const config = usePlatform();
  const token = useVybeAuthStore((s) => s.token);
  const [credits, setCredits] = useState<AiCredits | null>(null);

  const refresh = useCallback(async () => {
    const bearer = getVybeToken();
    if (!bearer) {
      setCredits(null);
      return;
    }
    try {
      const res = await fetch(resolveLlmUrl("/api/agent/credits", config), {
        headers: { Authorization: `Bearer ${bearer}` },
      });
      if (!res.ok) return;
      setCredits((await res.json()) as AiCredits);
    } catch {
      // best-effort; the meter is a hint, not a gate (the server enforces the cap)
    }
  }, [config]);

  useEffect(() => {
    void refresh();
  }, [refresh, token]);

  return { credits, refresh };
}
